import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Zap, Cpu, Loader2, Wand2, Sparkles, Play, Square, Eraser, Dice5, Coins, FileMusic, Download } from "lucide-react";
import { parseMidi, downloadMidi } from "@/lib/midi";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useGenerations from "@/hooks/useGenerations";
import GenerationCard from "@/components/GenerationCard";
import PatchBrowser from "@/components/PatchBrowser";
import StepGrid from "@/components/StepGrid";
import { recordAiPreference } from "@/components/ModelPreference";

const KINDS = [["melody", "Mélodie"], ["arp", "Arpège"], ["chords", "Accords"], ["pad", "Nappe"], ["bass", "Basse"], ["drums", "Batterie"]];
const ROOTS = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const SCALES = ["minor", "major", "phrygian", "dorian", "lydian", "pentatonic", "minor_pent"];

export default function Synth() {
  const { refreshUser } = useAuth();
  const { generations, refetch } = useGenerations();
  const [status, setStatus] = useState(null);
  const [patch, setPatch] = useState(null);
  const [notes, setNotes] = useState([]);
  const [bars, setBars] = useState(2);
  const [bpm, setBpm] = useState(120);
  const [duration, setDuration] = useState(8);
  const [loop, setLoop] = useState(true);
  const [repeat, setRepeat] = useState(true);
  const [title, setTitle] = useState("");
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("none");
  const [kind, setKind] = useState("arp");
  const [root, setRoot] = useState("A");
  const [scale, setScale] = useState("minor");
  const [prompt, setPrompt] = useState("");
  const [provider, setProvider] = useState("openai");
  const [busy, setBusy] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const ctxRef = useRef(null);

  useEffect(() => {
    api.get("/synth/status").then(({ data }) => setStatus(data)).catch(() => {});
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  const isDrums = kind === "drums" || /drum|kit|perc/i.test(patch?.category || "") || /kit/i.test(patch?.name || "");
  const [lo, hi] = useMemo(() => {
    if (isDrums) return [36, 47];
    if (!notes.length) return [48, 72];
    const ps = notes.map((n) => n.pitch);
    return [Math.max(12, Math.min(...ps) - 3), Math.min(120, Math.max(...ps) + 3)];
  }, [notes, isDrums]);

  const generatePattern = async () => {
    setBusy("pattern");
    try {
      const { data } = await api.post("/synth/pattern", { prompt: `${kind} ${root} ${scale} ${Date.now() % 1000}`, kind, bars, root, scale });
      setNotes(data.notes);
      toast.success(`Motif ${kind} généré (${data.notes.length} notes)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Génération impossible."); }
    finally { setBusy(null); }
  };

  const composeAI = async () => {
    if (!prompt.trim()) return;
    setBusy("ai");
    try {
      const { data } = await api.post("/synth/ai-notes", { prompt: prompt.trim(), kind, bars, root, scale, provider });
      setNotes(data.notes);
      recordAiPreference(provider, "synth");
      toast.success(`${data.notes.length} notes composées par ${provider === "openai" ? "ChatGPT" : "Claude"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Le compositeur IA est indisponible."); }
    finally { setBusy(null); }
  };

  const stopPreview = () => { try { ctxRef.current?.close(); } catch { /* noop */ } ctxRef.current = null; setPlaying(false); };
  const preview = () => {
    if (playing) return stopPreview();
    if (!notes.length) return;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctxRef.current = ctx;
    const spb = 60 / bpm;
    const master = ctx.createGain(); master.gain.value = 0.25; master.connect(ctx.destination);
    let end = 0;
    notes.forEach((n) => {
      const t0 = ctx.currentTime + 0.05 + n.start * spb;
      const dur = Math.max(0.05, n.duration * spb);
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      if (isDrums) {
        osc.type = "triangle";
        osc.frequency.setValueAtTime(n.pitch % 12 === 0 ? 150 : n.pitch % 12 === 2 ? 220 : 900, t0);
        osc.frequency.exponentialRampToValueAtTime(n.pitch % 12 === 0 ? 45 : 200, t0 + 0.12);
      } else {
        osc.type = "sawtooth";
        osc.frequency.value = 440 * 2 ** ((n.pitch - 69) / 12);
      }
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime((n.velocity || 100) / 127, t0 + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      osc.connect(g).connect(master);
      osc.start(t0); osc.stop(t0 + dur + 0.05);
      end = Math.max(end, t0 + dur + 0.1);
    });
    setPlaying(true);
    setTimeout(() => { if (ctxRef.current === ctx) stopPreview(); }, (end - ctx.currentTime) * 1000);
  };

  const importMidi = async (file) => {
    if (!file) return;
    try {
      const { notes: ns, bpm: b, tracks } = parseMidi(await file.arrayBuffer());
      if (!ns.length) { toast.error("Aucune note trouvée dans ce fichier MIDI."); return; }
      const lastBeat = Math.max(...ns.map((n) => n.start + n.duration));
      setNotes(ns);
      setBars(Math.min(4, Math.max(1, Math.ceil(lastBeat / 4))));
      if (b >= 40 && b <= 240) setBpm(b);
      setDuration((d) => Math.max(d, Math.min(60, Math.ceil((lastBeat * 60) / (b || 120)) + 1)));
      setTitle((t) => t || file.name.replace(/\.midi?$/i, ""));
      toast.success(`${ns.length} notes importées (${tracks} piste${tracks > 1 ? "s" : ""}, ${b} BPM)`);
    } catch (e) { toast.error(e.message || "Fichier MIDI illisible."); }
  };
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); importMidi(e.dataTransfer.files?.[0]); };

  const render = async () => {
    if (!patch) { toast.error("Choisissez un patch."); return; }
    if (!notes.length) { toast.error("Ajoutez des notes (grille, motif ou IA)."); return; }
    setBusy("render");
    try {
      await api.post("/synth/render", { patch_id: patch.id, notes, bpm, duration, title: title.trim(), project_id: projectId === "none" ? null : projectId, loop, repeat });
      await refreshUser();
      await refetch();
      window.dispatchEvent(new Event("synth-rendered"));
      toast.success(`Rendu ${status?.engine === "surge" ? "Surge XT" : "synthé interne"} lancé — 1 crédit.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Rendu impossible."); }
    finally { setBusy(null); }
  };

  const onDelete = async (id) => { try { await api.delete(`/generations/${id}`); await refetch(); } catch { toast.error("Suppression impossible."); } };
  const synthGens = generations.filter((g) => g.kind === "synth");
  const surge = status?.engine === "surge";

  return (
    <div className="space-y-6" data-testid="synth-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">Surge XT <span className="text-muted-foreground font-normal text-lg">Synthé</span></h1>
          <p className="text-sm text-muted-foreground mt-1">Rendu serveur headless du synthé open source Surge XT (GPL-3, gratuit) : 3500+ patches, séquences en piano-roll, composition IA. 1 crédit par rendu.</p>
        </div>
        {status && (
          <div className={`text-xs font-mono px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 ${surge ? "border-amber-500/40 text-amber-400 bg-amber-500/10" : "border-border text-muted-foreground"}`} data-testid="synth-engine-badge">
            {surge ? <Zap size={12} /> : <Cpu size={12} />} {surge ? `Surge XT ${status.surge_version?.split(".").slice(0, 2).join(".")} · ${status.patch_count} patches` : `Synthé interne (repli) · ${status.patch_count} patches`}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 min-w-0">
          <PatchBrowser value={patch} onChange={setPatch} categories={status?.categories || []} />
          {patch && <div className="mt-2 text-xs text-muted-foreground" data-testid="selected-patch">Patch : <span className="text-primary font-medium">{patch.name}</span> <span className="font-mono">· {patch.category} · {patch.engine === "surge" ? "Surge XT" : "interne"}</span></div>}
        </div>

        <div className="lg:col-span-3 space-y-4 min-w-0">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}
            className={`rounded-2xl border bg-card p-4 space-y-3 transition-colors ${dragOver ? "border-amber-400 bg-amber-500/[0.06]" : "border-border"}`} data-testid="composer-panel">
            {dragOver && <div className="text-xs font-mono text-amber-400 flex items-center gap-1.5" data-testid="midi-drop-hint"><FileMusic size={12} /> Déposez le fichier .mid pour l'importer</div>}
            <div className="flex flex-wrap items-center gap-2">
              <Select value={kind} onValueChange={setKind}><SelectTrigger className="h-8 w-32 text-xs bg-background border-border" data-testid="synth-kind"><SelectValue /></SelectTrigger>
                <SelectContent className="glass border-border">{KINDS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
              <Select value={root} onValueChange={setRoot}><SelectTrigger className="h-8 w-20 text-xs bg-background border-border" data-testid="synth-root"><SelectValue /></SelectTrigger>
                <SelectContent className="glass border-border">{ROOTS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select>
              <Select value={scale} onValueChange={setScale}><SelectTrigger className="h-8 w-32 text-xs bg-background border-border" data-testid="synth-scale"><SelectValue /></SelectTrigger>
                <SelectContent className="glass border-border">{SCALES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              <Select value={String(bars)} onValueChange={(v) => setBars(Number(v))}><SelectTrigger className="h-8 w-24 text-xs bg-background border-border" data-testid="synth-bars"><SelectValue /></SelectTrigger>
                <SelectContent className="glass border-border">{[1, 2, 4].map((b) => <SelectItem key={b} value={String(b)}>{b} mesure{b > 1 ? "s" : ""}</SelectItem>)}</SelectContent></Select>
              <Button size="sm" variant="outline" onClick={generatePattern} disabled={busy === "pattern"} className="h-8 gap-1.5 border-secondary/40 text-secondary hover:bg-secondary/10" data-testid="synth-pattern">
                {busy === "pattern" ? <Loader2 className="animate-spin" size={13} /> : <Dice5 size={13} />} Motif auto (gratuit)
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && composeAI()} placeholder="Décrivez la phrase : « arpège mystérieux qui monte », « basse funky »…" className="flex-1 min-w-[200px] h-8 px-3 rounded-md bg-background border border-border text-xs focus:outline-none focus:ring-1 focus:ring-primary" data-testid="synth-ai-prompt" />
              <button type="button" onClick={() => setProvider((p) => (p === "openai" ? "claude" : "openai"))} className="text-[10px] font-mono px-2.5 py-1 rounded-full bg-primary/15 text-primary border border-primary/30" data-testid="synth-ai-provider">{provider === "openai" ? "ChatGPT" : "Claude"}</button>
              <Button size="sm" onClick={composeAI} disabled={busy === "ai" || !prompt.trim()} className="h-8 gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="synth-ai-compose">
                {busy === "ai" ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />} Composer par IA
              </Button>
            </div>
            <StepGrid notes={notes} onChange={setNotes} bars={bars} lo={lo} hi={hi} drums={isDrums} />
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-mono text-muted-foreground" data-testid="synth-note-count">{notes.length} note{notes.length > 1 ? "s" : ""}</span>
              <Button size="sm" variant="ghost" onClick={preview} disabled={!notes.length} className="h-7 gap-1.5" data-testid="synth-preview">{playing ? <Square size={12} /> : <Play size={12} />} {playing ? "Stop" : "Pré-écoute (navigateur)"}</Button>
              <Button size="sm" variant="ghost" onClick={() => setNotes([])} disabled={!notes.length} className="h-7 gap-1.5 text-muted-foreground" data-testid="synth-clear"><Eraser size={12} /> Effacer</Button>
              <Button size="sm" variant="ghost" onClick={() => downloadMidi(notes, bpm, title || `${patch?.name || "pattern"}-${bpm}bpm`)} disabled={!notes.length} className="h-7 gap-1.5 ml-auto" title="Télécharger le motif en .mid (Surge XT, DAW)" data-testid="midi-export"><Download size={12} /> Exporter MIDI</Button>
              <label className="h-7 px-2.5 rounded-md border border-border text-xs inline-flex items-center gap-1.5 cursor-pointer hover:bg-muted" title="Importer un fichier MIDI (.mid) — ou glissez-le sur ce panneau" data-testid="midi-import-label">
                <FileMusic size={12} /> Importer MIDI
                <input type="file" accept=".mid,.midi,audio/midi" className="hidden" onChange={(e) => { importMidi(e.target.files?.[0]); e.target.value = ""; }} data-testid="midi-import-input" />
              </label>
            </div>
          </motion.div>

          <div className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid="render-panel">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-1.5"><div className="flex justify-between text-xs"><Label>Tempo</Label><span className="font-mono text-primary" data-testid="synth-bpm-value">{bpm} BPM</span></div>
                <Slider value={[bpm]} min={40} max={240} step={1} onValueChange={(v) => setBpm(v[0])} data-testid="synth-bpm" /></div>
              <div className="space-y-1.5"><div className="flex justify-between text-xs"><Label>Durée</Label><span className="font-mono text-primary" data-testid="synth-duration-value">{duration}s</span></div>
                <Slider value={[duration]} min={2} max={60} step={1} onValueChange={(v) => setDuration(v[0])} data-testid="synth-duration" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Projet</Label>
                <Select value={projectId} onValueChange={setProjectId}><SelectTrigger className="h-8 text-xs bg-background border-border" data-testid="synth-project"><SelectValue /></SelectTrigger>
                  <SelectContent className="glass border-border"><SelectItem value="none">Aucun projet</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre de la piste (optionnel)" maxLength={80} className="flex-1 min-w-[180px] h-8 px-3 rounded-md bg-background border border-border text-xs focus:outline-none focus:ring-1 focus:ring-primary" data-testid="synth-title" />
              <label className="flex items-center gap-2 text-xs"><Switch checked={repeat} onCheckedChange={setRepeat} data-testid="synth-repeat" /> Répéter le motif</label>
              <label className="flex items-center gap-2 text-xs"><Switch checked={loop} onCheckedChange={setLoop} data-testid="synth-loop" /> Version bouclable</label>
              <Button onClick={render} disabled={busy === "render"} className="ml-auto bg-secondary text-white hover:bg-secondary/90 gap-2" data-testid="synth-render">
                {busy === "render" ? <Loader2 className="animate-spin" size={15} /> : <Wand2 size={15} />} Rendre {surge ? "avec Surge XT" : ""} <span className="inline-flex items-center gap-1 text-xs opacity-80"><Coins size={11} /> 1 cr</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <section>
        <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2">Rendus synthé <span className="text-xs font-mono text-muted-foreground" data-testid="synth-gens-count">{synthGens.length}</span></h2>
        {synthGens.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="synth-empty">Aucun rendu pour l'instant — choisissez un patch, un motif, puis « Rendre ».</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="synth-gens">
            {synthGens.map((g) => <GenerationCard key={g.id} gen={g} onVariation={() => toast.message("Les variations s'appliquent aux pistes IA — relancez un rendu avec un autre patch.")} onDelete={onDelete} onSaved={refetch} />)}
          </div>
        )}
      </section>
    </div>
  );
}
